import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { MdAdd } from 'react-icons/md';

import { parseISO, subYears } from 'date-fns';
import Toast from '~/utils/toast';

import { Container } from './styles';
import Input from '~/components/Input';
import InputMask from '~/components/InputMask';
import InputDate from '~/components/InputDate';
import InputCheckbox from '~/components/InputCheckbox';
import { KrTrash } from '~/components/KoroIcons';
import { IOption } from '~/components/Select';
import api from '~/services/api';

export interface IRepresentative {
  id: number;
  name: string;
  email: string;
  cpf: string;
  birthdate: Date;
  gender: string;
  zipcode: string;
  street: string;
  number: string;
  city: string;
  state: string;
  neighborhood: string;
  complement?: string;
}

interface IProfileUser {
  profile: {
    environment: string;
    name: string;
  };
}

interface IUserData {
  id: number;
  name: string;
  email: string;
  profilesUsers: IProfileUser[];
  additionalInformation: {
    document: string;
    phone: string;
    birthdate: string;
    gender: string;
    address: {
      street: string;
      number: string;
      neighborhood: string;
      city: string;
      state: string;
      country: string;
      complement?: string;
      zip_code: string;
      full_address?: string;
    };
  };
}

interface IRepresentativesProps {
  onChange(representatives: IRepresentative[]): void;
  setDocumentError: React.Dispatch<React.SetStateAction<boolean>>;
}

const Representative: React.FC<IRepresentativesProps> = ({
  onChange,
  setDocumentError,
}) => {
  const [representatives, setRepresentatives] = useState<IRepresentative[]>([
    {
      id: 1,
      name: '',
      email: '',
      cpf: '',
      birthdate: subYears(new Date(), 18),
      gender: '',
      zipcode: '',
      street: '',
      number: '',
      city: '',
      state: '',
      neighborhood: '',
      complement: '',
    },
  ]);

  const handleClickAddRepresentative = useCallback(() => {
    setRepresentatives([
      ...representatives,
      {
        id: representatives.length + 1,
        name: '',
        email: '',
        cpf: '',
        birthdate: subYears(new Date(), 18),
        gender: '',
        zipcode: '',
        street: '',
        number: '',
        city: '',
        state: '',
        neighborhood: '',
        complement: '',
      },
    ]);
  }, [representatives]);

  const handleChangeEmail = useCallback(async (e, index) => {
    const email = e.target.value;
    setRepresentatives((oldState) => {
      const newRepresentatives = [...oldState];
      newRepresentatives[index] = {
        ...newRepresentatives[index],
        email,
      };

      return newRepresentatives;
    });

    const response = await api.get<IUserData>(`users/emails/${email}`);

    if (response.data) {
      setRepresentatives((oldState) => {
        const newRepresentatives = [...oldState];
        newRepresentatives[index] = {
          ...newRepresentatives[index],
          name: response.data.name,
          cpf: response.data.additionalInformation.document,
          birthdate: response.data.additionalInformation.birthdate
            ? parseISO(response.data.additionalInformation.birthdate)
            : subYears(new Date(), 18),
          gender: response.data.additionalInformation.gender,
          zipcode: response.data.additionalInformation.address?.zip_code,
          street: response.data.additionalInformation.address?.street,
          number: response.data.additionalInformation.address?.number,
          city: response.data.additionalInformation.address?.city,
          state: response.data.additionalInformation.address?.state,
          neighborhood:
            response.data.additionalInformation.address?.neighborhood,
          complement: response.data.additionalInformation.address?.complement,
          email: e.target.value,
        };

        return newRepresentatives;
      });
    }
  }, []);

  const handleChangeName = useCallback((e, index) => {
    setRepresentatives((oldState) => {
      const newRepresentatives = [...oldState];
      newRepresentatives[index] = {
        ...newRepresentatives[index],
        name: e.target.value,
      };

      return newRepresentatives;
    });
  }, []);

  const handleChangeCpf = useCallback(
    async (e, index) => {
      setDocumentError(false);
      if (e.target.value.length === 14) {
        try {
          const response = await api.get(
            `additional-informations/document/${e.target.value}`
          );
          if (response.data) {
            setDocumentError(true);
            Swal.fire({
              icon: 'error',
              title: 'CPF já cadastrado',
              text: 'O CPF informado já está cadastrado.',
            });
          }
        } catch (error) {
          // console.log(error);
        }
      }
      setRepresentatives((oldState) => {
        const newRepresentatives = [...oldState];
        newRepresentatives[index] = {
          ...newRepresentatives[index],
          cpf: e.target.value,
        };

        return newRepresentatives;
      });
    },
    [setDocumentError]
  );

  const handleChangeBirthdate = useCallback((e, index) => {
    setRepresentatives((oldState) => {
      const newRepresentatives = [...oldState];
      newRepresentatives[index] = {
        ...newRepresentatives[index],
        birthdate: e,
      };

      return newRepresentatives;
    });
  }, []);

  const handleChangeGender = useCallback((options, index) => {
    const optionSelected = options.find((option: IOption) => option.selected);
    if (optionSelected) {
      setRepresentatives((oldState) => {
        const newRepresentatives = [...oldState];
        newRepresentatives[index] = {
          ...oldState[index],
          gender: optionSelected.value,
        };

        return newRepresentatives;
      });
    }
  }, []);

  const handleChangeZipCode = useCallback(async (e, index) => {
    const { value } = e.target;

    if (value.length === 9) {
      try {
        const response = await axios.get(
          `https://viacep.com.br/ws/${value}/json/`
        );

        setRepresentatives((oldState) => {
          const newRepresentatives = [...oldState];
          newRepresentatives[index] = {
            ...newRepresentatives[index],
            zipcode: value,
            street: response.data.logradouro,
            neighborhood: response.data.bairro,
            city: response.data.localidade,
            state: response.data.uf,
            complement: response.data.complemento,
          };

          return newRepresentatives;
        });
      } catch (error) {
        // console.error('Erro ao buscar CEP:', error);
      }
    } else {
      setRepresentatives((oldState) => {
        const newRepresentatives = [...oldState];
        newRepresentatives[index] = {
          ...newRepresentatives[index],
          zipcode: value,
        };

        return newRepresentatives;
      });
    }
  }, []);

  const handleChangeStreet = useCallback((e, index) => {
    setRepresentatives((oldState) => {
      const newRepresentatives = [...oldState];
      newRepresentatives[index] = {
        ...newRepresentatives[index],
        street: e.target.value,
      };

      return newRepresentatives;
    });
  }, []);

  const handleChangeNumber = useCallback((e, index) => {
    setRepresentatives((oldState) => {
      const newRepresentatives = [...oldState];
      newRepresentatives[index] = {
        ...newRepresentatives[index],
        number: e.target.value,
      };

      return newRepresentatives;
    });
  }, []);

  const handleChangeCity = useCallback((e, index) => {
    setRepresentatives((oldState) => {
      const newRepresentatives = [...oldState];
      newRepresentatives[index] = {
        ...newRepresentatives[index],
        city: e.target.value,
      };

      return newRepresentatives;
    });
  }, []);

  const handleChangeState = useCallback((e, index) => {
    setRepresentatives((oldState) => {
      const newRepresentatives = [...oldState];
      newRepresentatives[index] = {
        ...newRepresentatives[index],
        state: e.target.value,
      };

      return newRepresentatives;
    });
  }, []);

  const handleChangeNeighborhood = useCallback((e, index) => {
    setRepresentatives((oldState) => {
      const newRepresentatives = [...oldState];
      newRepresentatives[index] = {
        ...newRepresentatives[index],
        neighborhood: e.target.value,
      };

      return newRepresentatives;
    });
  }, []);

  const handleChangeComplement = useCallback((e, index) => {
    setRepresentatives((oldState) => {
      const newRepresentatives = [...oldState];
      newRepresentatives[index] = {
        ...newRepresentatives[index],
        complement: e.target.value,
      };

      return newRepresentatives;
    });
  }, []);

  const handleClickDelete = useCallback((organizer) => {
    Swal.fire({
      icon: 'warning',
      iconColor: '#ff6900',
      title: `Excluir representante?`,
      showCancelButton: true,
      cancelButtonText: 'Não',
      confirmButtonText: 'Sim',
      confirmButtonColor: '#ff6900',
      cancelButtonColor: '#B34A00',
      reverseButtons: true,
    }).then((e) => {
      if (e.isConfirmed) {
        setRepresentatives((oldState) =>
          oldState.filter((organizerData) => organizerData.id !== organizer.id)
        );
        Toast.fire({
          icon: 'success',
          iconColor: '#ff6900',
          title: 'Representante excluído com sucesso!',
        });
      }
    });
  }, []);

  useEffect(() => {
    onChange(representatives);
  }, [onChange, representatives]);

  return (
    <Container className="row">
      {representatives.map((representative, index) => (
        <>
          {index > 0 && <hr className="mt-4" />}
          <div className="col-12 d-flex justify-content-between align-items-center mb-3">
            <h3 className="h5 fw-semibold mb-0">Representante {index + 1}</h3>
            {representatives.length > 1 && (
              <button
                type="button"
                onClick={() => handleClickDelete(representative)}
                className="bg-transparent border-0"
              >
                <KrTrash size={16} color="#f00" />
              </button>
            )}
          </div>
          <div className="col-lg-6">
            <h3 className="h6 fw-normal">E-mail</h3>
            <Input
              type="email"
              name={`email_${representative.id}`}
              value={representative.email}
              onChange={(e) => handleChangeEmail(e, index)}
              className="input"
            />
          </div>
          <div className="col-lg-6">
            <h3 className="h6 fw-normal">Nome</h3>
            <Input
              name={`name_${representative.id}`}
              value={representative.name}
              onChange={(e) => handleChangeName(e, index)}
              className="input"
            />
          </div>
          <div className="col-lg-4 mt-3">
            <h3 className="h6 fw-normal">CPF</h3>
            <InputMask
              kind="cpf"
              name={`cpf_${representative.id}`}
              value={representative.cpf}
              onChange={(e) => handleChangeCpf(e, index)}
              className="input"
            />
          </div>
          <div className="col-lg-3 mt-3">
            <h3 className="h6 fw-normal">Data nascimento</h3>
            <InputDate
              className="py-1"
              name={`birthdate_${representative.id}`}
              selected={representative.birthdate}
              onChange={(e) => handleChangeBirthdate(e, index)}
              colorSvg="#5D5D5D"
            />
          </div>
          <div className="col-lg-5 mt-3">
            <h3 className="h6 fw-normal">Gênero</h3>
            <InputCheckbox
              name={`gender_${representative.id}`}
              type="radio"
              className="input-radio"
              options={[
                {
                  label: 'Masculino',
                  value: 'M',
                  selected: representative.gender === 'M',
                },
                {
                  label: 'Feminino',
                  value: 'F',
                  selected: representative.gender === 'F',
                },
              ]}
              onChange={(e) => handleChangeGender(e, index)}
            />
          </div>
          <div className="col-lg-4 mt-3">
            <h3 className="h6 fw-normal">CEP</h3>
            <InputMask
              kind="zip-code"
              name={`zipcode_${representative.id}`}
              value={representative.zipcode}
              onChange={(e) => handleChangeZipCode(e, index)}
              className="input"
            />
          </div>
          <div className="col-lg-8 mt-3">
            <h3 className="h6 fw-normal">Endereço</h3>
            <Input
              name={`street_${representative.id}`}
              value={representative.street}
              onChange={(e) => handleChangeStreet(e, index)}
              className="input"
            />
          </div>
          <div className="col-lg-2 mt-3">
            <h3 className="h6 fw-normal">Número</h3>
            <Input
              name={`number_${representative.id}`}
              value={representative.number}
              onChange={(e) => handleChangeNumber(e, index)}
              className="input"
            />
          </div>
          <div className="col-lg-5 mt-3">
            <h3 className="h6 fw-normal">Cidade</h3>
            <Input
              name={`city_${representative.id}`}
              value={representative.city}
              onChange={(e) => handleChangeCity(e, index)}
              className="input"
            />
          </div>
          <div className="col-lg-5 mt-3">
            <h3 className="h6 fw-normal">Estado</h3>
            <Input
              name={`state_${representative.id}`}
              value={representative.state}
              onChange={(e) => handleChangeState(e, index)}
              className="input"
            />
          </div>
          <div className="col-lg-5 mt-3">
            <h3 className="h6 fw-normal">Bairro</h3>
            <Input
              name={`neighborhood_${representative.id}`}
              value={representative.neighborhood}
              onChange={(e) => handleChangeNeighborhood(e, index)}
              className="input"
            />
          </div>
          <div className="col-lg-7 mt-3">
            <h3 className="h6 fw-normal">Complemento</h3>
            <Input
              name={`complement_${representative.id}`}
              value={representative.complement}
              onChange={(e) => handleChangeComplement(e, index)}
              className="input"
            />
          </div>
        </>
      ))}
      <div className="col-12 d-flex mt-3">
        <button
          onClick={handleClickAddRepresentative}
          type="button"
          className="h6 fw-normal add-representative border-0 bg-transparent"
        >
          <MdAdd size={25} color="#ff6900" /> Adicionar outro representante
        </button>{' '}
      </div>
    </Container>
  );
};

export default Representative;
